<template>
  <b-modal ref="modal" size="xl" hide-footer scrollable
           :modal-class="modalClass"
           body-class="itm-body" header-class="itm-header" @hidden="onHidden">
    <!-- Title bar carries the totals of the WHOLE filtered set: count (real
         FTDs when grouping, matching the KPI card) + sum incl. family rows -->
    <template #modal-title>
      <div class="itm-head">
        <span class="itm-head__title">{{ title }}</span>
        <span class="itm-head__chips" v-if="visible">
          <span class="itm-chip itm-chip--count">
            <span class="itm-chip__label">{{ groupFtdFamily ? $t('salesDashboard.ftd') : $t('menu.monetaries') }}</span>
            <span class="itm-chip__value">{{ groupFtdFamily && ftdCount != null ? ftdCount : totalCount }}</span>
          </span>
          <span class="itm-chip itm-chip--sum">
            <span class="itm-chip__label">{{ sumLabel }}</span>
            <span class="itm-chip__value">{{ convertToEURO(displaySum) }}</span>
          </span>
          <!-- Pending drill: ONE task covering the whole pending set -->
          <button v-if="isPendingDrill" type="button" class="itm-chip itm-chip--task"
                  v-b-tooltip.hover :title="$t('tasks.taskForAll')" @click="createTaskForAll">
            <i class="fa-solid fa-list-check mr-25"></i>{{ $t('tasks.taskForAll') }}
          </button>
        </span>
      </div>
    </template>
    <!-- Column headers are draggable: drop one onto another to reorder. The
         order is remembered in the browser (localStorage). -->
    <div class="itm-table" @dragstart="onColDragStart" @dragover.prevent @drop.prevent="onColDrop">
      <div class="itm-colhint">
        <span class="itm-colhint__pill"><i class="fa-solid fa-left-right mr-25"></i>{{ $t('columns.dragToReorder') }}</span>
        <button v-if="columnOrder.length" type="button" class="itm-colhint__reset" @click="resetColumnOrder">
          <i class="fa-solid fa-rotate-left mr-25"></i>{{ $t('columns.resetOrder') }}
        </button>
      </div>
      <monetaries-table v-if="visible" :filters="filters" :columns="columns" :has-selection="false" :lazy="false"
                        :group-ftd-family="groupFtdFamily"
                        @total="v => totalCount = v" @sum="v => sum = v" @ftd-count="v => ftdCount = v"/>
    </div>
    <task-modal ref="taskModal"/>
  </b-modal>
</template>

<script>
import MonetariesTable from '@/components/tables/MonetariesTable';
import TaskModal from '@/components/modal/TaskModal.vue';
import mixinBase from '@/components/mixin/mixinBase';

const COLUMN_ORDER_KEY = 'insightTx.columnOrder';
function readColumnOrder() {
  try {
    const raw = localStorage.getItem(COLUMN_ORDER_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter(k => typeof k === 'string') : [];
  } catch (e) {
    return [];
  }
}
function writeColumnOrder(keys) {
  try {
    if (keys && keys.length) localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(keys));
    else localStorage.removeItem(COLUMN_ORDER_KEY);
  } catch (e) { /* private mode */ }
}

/**
 * Drill-down used by the Client/User Insights stat cards: shows the raw
 * monetary transactions behind a KPI, using the standard MonetariesTable.
 * For the FTD-family drill (ftdFamily filter) the rows are grouped per
 * client — his FTD_A/B… deposits expand under his FTD row.
 */
export default {
  name: 'InsightTransactionsModal',
  components: { MonetariesTable, TaskModal },
  mixins: [mixinBase],
  props: {
    // Optional extra class on the modal — lets a host page (e.g. the Command
    // Center) apply its own contrast skin without affecting other pages.
    modalClass: { type: String, default: '' },
  },
  data: () => ({
    visible: false,
    title: '',
    filters: {},
    totalCount: 0,
    sum: 0,
    ftdCount: null,
    // User-chosen column order (keys), persisted per browser
    columnOrder: readColumnOrder(),
    dragKey: null,
  }),
  computed: {
    groupFtdFamily() {
      return this.filters && this.filters.ftdFamily === true;
    },
    // Withdrawal drills (incl. pending withdrawals): label the sum correctly
    // and show it positive — stored withdrawal amounts are negative.
    isWithdrawalDrill() {
      return this.filters && this.filters.type === 'WITHDRAWAL';
    },
    sumLabel() {
      return this.isWithdrawalDrill ? this.$t('Total Withdrawals') : this.$t('monetary.totalDeposits');
    },
    displaySum() {
      return this.isWithdrawalDrill ? Math.abs(this.sum || 0) : (this.sum || 0);
    },
    // Drill on REQUEST/PENDING rows (the pending-approvals KPI cards) —
    // those lists also get the processor details and the actions column
    // (approve/decline/task), so pending money can be worked right here.
    isPendingDrill() {
      const status = this.filters && this.filters.status;
      return Array.isArray(status) && (status.includes('REQUEST') || status.includes('PENDING'));
    },
    columns() {
      const cols = [
        { key: 'id', enable: true },
        { key: 'clientDetails', enable: true },
        { key: 'clientDetails.accountId', enable: true },
        // Every KPI drill (all dashboards) shows the business unit of the row
        { key: 'businessUnit', enable: true },
        { key: 'amount', enable: true },
        { key: 'currency', enable: true },
        { key: 'type', enable: true },
        { key: 'action', enable: true },
        { key: 'depositorUserId', enable: true },
        { key: 'isFtd', enable: true },
        { key: 'registrationTime', enable: true },
        { key: 'status', enable: true },
        // processor + payment comments — important context for pending review
        { key: 'paymentDetails.processorName', enable: true },
        { key: 'paymentDetails.comments', enable: true },
      ];
      if (this.groupFtdFamily) {
        // FTD vs FTD_A/B… distinction next to the isFtd flag
        cols.splice(9, 0, { key: 'ftdType', enable: true });
      }
      if (this.isPendingDrill) {
        cols.push({ key: 'actions', enable: true });
      }
      // Apply the remembered order (unknown/new keys keep their default place at the end)
      if (this.columnOrder.length) {
        const rank = key => {
          const i = this.columnOrder.indexOf(key);
          return i === -1 ? this.columnOrder.length + cols.findIndex(c => c.key === key) : i;
        };
        cols.sort((a, b) => rank(a.key) - rank(b.key));
      }
      // Draggable headers (MonetariesTable passes extra column props through to b-table)
      return cols.map(c => ({ ...c, thAttr: { draggable: 'true', 'data-col-key': c.key } }));
    },
  },
  methods: {
    // ---- column reorder by dragging the header cells ----
    onColDragStart(event) {
      const th = event.target && event.target.closest ? event.target.closest('th[data-col-key]') : null;
      if (!th) return;
      this.dragKey = th.getAttribute('data-col-key');
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', this.dragKey);
      }
    },
    onColDrop(event) {
      const th = event.target && event.target.closest ? event.target.closest('th[data-col-key]') : null;
      const from = this.dragKey;
      this.dragKey = null;
      if (!th || !from) return;
      const to = th.getAttribute('data-col-key');
      if (!to || to === from) return;
      const keys = this.columns.map(c => c.key);
      const fromIdx = keys.indexOf(from);
      const toIdx = keys.indexOf(to);
      if (fromIdx === -1 || toIdx === -1) return;
      keys.splice(fromIdx, 1);
      keys.splice(toIdx, 0, from);
      this.columnOrder = keys;
      writeColumnOrder(keys);
    },
    resetColumnOrder() {
      this.columnOrder = [];
      writeColumnOrder([]);
    },
    // ONE task covering the whole pending set shown by this drill —
    // count + total in the title, scope details in the description.
    createTaskForAll() {
      const f = this.filters || {};
      const range = f.registrationTime || {};
      this.$refs.taskModal.showModal(null, {
        type: 'HANDLE_TRANSACTION',
        relatedModule: 'MONETARY',
        title: `${this.title} · ${this.totalCount} · ${this.convertToEURO(this.displaySum)}`,
        description: [
          `${this.$t('menu.monetaries')}: ${this.totalCount}`,
          `${this.$t('salesDashboard.total')}: ${this.convertToEURO(this.displaySum)}`,
          f.type ? `${this.$t('columns.type')}: ${f.type}` : null,
          range.start || range.end ? `${this.$t('filters.registrationTime')}: ${range.start || ''} → ${range.end || ''}` : null,
        ].filter(Boolean).join('\n'),
        // The exact drill scope — the task detail re-queries it LIVE and
        // offers the standard table + bulk actions on those transactions.
        context: { monetaryFilters: { ...f } },
      });
    },
    show(title, filters) {
      this.title = title;
      this.filters = filters || {};
      this.totalCount = 0;
      this.sum = 0;
      this.ftdCount = null;
      this.visible = true;
      this.$refs.modal.show();
    },
    onHidden() {
      this.visible = false;
      this.filters = {};
    },
  },
};
</script>

<style lang="scss">
.itm-body {
  min-height: 320px;
  /* Never outgrow the viewport: header/title and the table's pagination must
     always stay reachable — the body scrolls internally instead. */
  max-height: calc(100vh - 190px);
  overflow-y: auto;
}
/* Title bar: title left, total chips right, close button untouched */
.itm-header .modal-title {
  flex: 1;
  min-width: 0;
}
.itm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;
  padding-right: 0.75rem;
}
.itm-head__title {
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.itm-head__chips {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.itm-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 0.45rem;
  padding: 0.22rem 0.75rem;
  border-radius: 999px;
  color: #fff;
  box-shadow: 0 4px 12px -6px rgba(20, 24, 50, 0.5);

  &--count { background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%); }
  &--sum { background: linear-gradient(135deg, #34d399 0%, #10b981 60%, #059669 100%); }
  /* "task for all" button-chip — same shape, clickable */
  &--task {
    background: linear-gradient(135deg, #8e7cf5 0%, #7367f0 60%, #5e50ee 100%);
    border: none;
    cursor: pointer;
    align-items: center;
    font-weight: 700;
    transition: filter 0.15s ease;
    &:hover, &:focus { filter: brightness(1.1); outline: none; color: #fff; }
  }

  &__label {
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    opacity: 0.9;
  }
  &__value {
    font-size: 0.95rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }
}
/* Draggable column headers + the hint bar above the table */
.itm-table {
  th[data-col-key] {
    cursor: grab;
    user-select: none;
    /* grip mark so the headers read as movable */
    &::before {
      content: '⋮⋮';
      display: inline-block;
      margin-right: 0.35rem;
      opacity: 0.45;
      font-size: 0.8em;
      letter-spacing: -2px;
    }
    &:hover::before { opacity: 0.9; }
  }
  th[data-col-key]:active { cursor: grabbing; }
}
.itm-colhint {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0 0 0.5rem;
  &__pill {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.7rem;
    border-radius: 999px;
    background: rgba(0, 123, 255, 0.1);
    border: 1px dashed rgba(0, 123, 255, 0.45);
    color: #007bff;
    font-size: 0.78rem;
    font-weight: 600;
  }
  &__reset {
    border: 1px solid rgba(0, 123, 255, 0.35);
    background: transparent;
    color: #007bff;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    cursor: pointer;
    &:hover { background: rgba(0, 123, 255, 0.1); }
  }
}
.dark-layout .itm-colhint__pill { background: rgba(0, 123, 255, 0.18); color: #8ec5ff; }
</style>
